import { useState, useEffect } from "react";
import { Ticket, Loader2, Trash2, Plus, Power } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";

const EMPTY = { code: "", type: "percent", value: 10, active: true, max_uses: 0, min_amount: 0, expires_at: "" };

export default function AdminCoupons() {
  const [coupons, setCoupons] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/coupons").then(({ data }) => setCoupons(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (form.code.trim().length < 3) { toast.error("Código muito curto"); return; }
    setSaving(true);
    try {
      await api.post("/coupons", { ...form, value: Number(form.value), max_uses: Number(form.max_uses), min_amount: Number(form.min_amount), expires_at: form.expires_at ? new Date(form.expires_at).toISOString() : null });
      toast.success("Cupom criado");
      setForm(EMPTY); load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setSaving(false); }
  };

  const toggle = async (c) => {
    try { await api.put(`/coupons/${c.id}`, { ...c, active: !c.active }); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const remove = async (c) => {
    try { await api.delete(`/coupons/${c.id}`); toast.success("Removido"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="max-w-4xl space-y-6" data-testid="admin-coupons">
      <div>
        <h2 className="font-heading text-2xl font-bold tracking-tight flex items-center gap-2"><Ticket className="h-6 w-6 text-primary" /> Cupons de desconto</h2>
        <p className="mt-1 text-sm text-muted-foreground">Crie cupons aplicáveis no checkout (web e bot).</p>
      </div>

      <div className="rounded-xl border border-border bg-card p-5">
        <h3 className="mb-3 font-heading font-semibold">Novo cupom</h3>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
          <div className="space-y-1 col-span-2 sm:col-span-1"><Label className="text-xs">Código</Label>
            <Input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} placeholder="VULPIX10" data-testid="coupon-code" /></div>
          <div className="space-y-1"><Label className="text-xs">Tipo</Label>
            <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
              <SelectTrigger data-testid="coupon-type"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="percent">%</SelectItem><SelectItem value="fixed">R$</SelectItem></SelectContent>
            </Select></div>
          <div className="space-y-1"><Label className="text-xs">Valor</Label>
            <Input type="number" value={form.value} onChange={(e) => setForm({ ...form, value: e.target.value })} data-testid="coupon-value" /></div>
          <div className="space-y-1"><Label className="text-xs">Máx. usos</Label>
            <Input type="number" value={form.max_uses} onChange={(e) => setForm({ ...form, max_uses: e.target.value })} data-testid="coupon-maxuses" /></div>
          <div className="space-y-1"><Label className="text-xs">Mín. R$</Label>
            <Input type="number" value={form.min_amount} onChange={(e) => setForm({ ...form, min_amount: e.target.value })} /></div>
          <div className="space-y-1"><Label className="text-xs">Expira</Label>
            <Input type="date" value={form.expires_at} onChange={(e) => setForm({ ...form, expires_at: e.target.value })} /></div>
        </div>
        <div className="mt-3 flex justify-end">
          <Button onClick={create} disabled={saving} data-testid="coupon-create-btn">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} Criar cupom
          </Button>
        </div>
      </div>

      <div className="space-y-2">
        {coupons.length === 0 && <p className="text-sm text-muted-foreground">Nenhum cupom ainda.</p>}
        {coupons.map((c) => (
          <div key={c.id} className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-border bg-card p-4" data-testid={`coupon-${c.code}`}>
            <div className="flex items-center gap-3">
              <span className="font-mono text-lg font-bold text-primary">{c.code}</span>
              <Badge variant="outline">{c.type === "percent" ? `${c.value}%` : `R$ ${c.value}`}</Badge>
              {c.active ? <Badge className="border-green-500/30 bg-green-500/15 text-green-600 dark:text-green-400" variant="outline">Ativo</Badge> : <Badge variant="secondary">Inativo</Badge>}
            </div>
            <div className="flex items-center gap-4 text-xs text-muted-foreground">
              <span>usos {c.uses}{c.max_uses ? `/${c.max_uses}` : ""}</span>
              {c.min_amount > 0 && <span>mín R$ {c.min_amount}</span>}
              <Button size="sm" variant="ghost" onClick={() => toggle(c)} data-testid={`coupon-toggle-${c.code}`}><Power className="h-4 w-4" /></Button>
              <Button size="sm" variant="ghost" className="text-destructive hover:bg-destructive/10" onClick={() => remove(c)} data-testid={`coupon-del-${c.code}`}><Trash2 className="h-4 w-4" /></Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
